import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
/* Navbar removed */
import { Footer } from "@/components/layout/Footer";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Link, useSearchParams } from "react-router-dom";

interface Story {
  id: number;
  circuit_id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image_url: string;
  category: string;
  read_time: string;
  published_date: string;
  featured: number; // API returns 0 or 1
}

const Blog = () => {
  const [posts, setPosts] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams(); // Moved here
  const circuitId = searchParams.get("circuit_id");

  useEffect(() => {
    fetchAPI("?type=stories")
      .then((data: any) => {
        setPosts(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching stories:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  // Hooks moved to top
  
  const filteredPosts = circuitId 
    ? posts.filter(p => p.circuit_id.toString() === circuitId)
    : posts;

  const featuredPost = !circuitId ? filteredPosts.find(p => p.featured == 1) : null;
  // If filtered by circuit, show all as regular posts (or keep featured logic if desired, but usually featured is "global")
  // Let's show all matching posts as grid items if filtered
  const regularPosts = circuitId 
    ? filteredPosts 
    : filteredPosts.filter(p => p.featured != 1);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Spiritual Blog" 
        description="Insights, guides, and reflections to prepare you for your spiritual journey and deepen your understanding of India's sacred traditions."
        canonical="https://baelleaves.com/stories"
      />


      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sand to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-saffron text-sm tracking-[0.2em] uppercase font-medium">
              Travel Journal
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 mb-6">
              Stories & <span className="text-maroon">Wisdom</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Insights, guides, and reflections to prepare you for your spiritual
              journey and deepen your understanding of India's sacred traditions.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-8">
          <div className="container mx-auto px-6 lg:px-8">
            <Link to={`/stories/${featuredPost.slug}`} className="group block">
              <article className="grid lg:grid-cols-2 gap-8 items-center bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500">
                <div className="relative h-64 lg:h-[400px] overflow-hidden">
                  <img
                    src={`https://baelleaves.com/${featuredPost.image_url}`}
                    alt={featuredPost.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-4 left-4 bg-saffron text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Featured
                  </span>
                </div>
                <div className="p-8 lg:py-12 flex flex-col justify-center">
                  <span className="text-gold text-sm font-medium mb-2">
                    {featuredPost.category}
                  </span>
                  <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-4 group-hover:text-saffron transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{featuredPost.published_date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{featuredPost.read_time}</span>
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          </div>
        </section>
      )}

      {/* Posts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <Link key={post.id} to={`/stories/${post.slug}`} className="group">
                <article className="bg-card rounded-2xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-500 hover:-translate-y-1">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={`https://baelleaves.com/${post.image_url}`}
                      alt={post.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-saffron text-xs font-medium">
                      {post.category}
                    </span>
                    <h3 className="font-serif text-lg text-foreground mt-2 mb-3 group-hover:text-saffron transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{post.read_time}</span>
                      <span className="text-saffron flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
